import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import { useHttpClient } from '../../contexts';
import { OrganizationUser } from './organizationUsers.models';
import {
  initState,
  organizationUsersReducer,
  OrganizationUsersState,
  type InviteFailure,
} from './organizationUsers.reducer';

interface InviteUsersResponse {
  failed: InviteFailure[];
}

type FetchUsersFilter = {
  userRoles?: OrganizationUser['userRole'][];
  status?: OrganizationUser['status'][];
};

interface OrganizationUsersContextProps {
  users: OrganizationUser[];
  currentUser: OrganizationUser | null;
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
  inviteFailures: InviteFailure[];
  fetchUsers: (
    organizationId: string,
    filters?: FetchUsersFilter
  ) => Promise<OrganizationUser[]>;
  fetchUserById: (userId: string) => Promise<void>;
  inviteUsers: (
    organizationId: string,
    invitedUsers: string[]
  ) => Promise<InviteFailure[]>;
  createUser: (
    userId: string,
    data: Partial<OrganizationUser>
  ) => Promise<InviteFailure[]>;
  updateUser: (
    userId: string,
    data: Partial<OrganizationUser>,
    imageProfileFile?: File
  ) => Promise<void>;
  setUserStatus: (
    userId: string,
    status: Extract<OrganizationUser['status'], 'ACTIVE' | 'SUSPENDED'>
  ) => Promise<void>;
  clearError: () => void;
}

const OrganizationUsersContext = createContext<
  OrganizationUsersContextProps | undefined
>(undefined);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<OrganizationUsersState>;
}>;

export const OrganizationUsersProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(organizationUsersReducer, {
    ...initState,
    ...initialState,
  });
  const httpClient = useHttpClient();

  const users = useMemo(
    () => state.ids.map((id) => state.entities[id]),
    [state.entities, state.ids]
  );

  const currentUser = useMemo(
    () => state.entities[state.currentUserId || ''],
    [state.currentUserId, state.entities]
  );

  const fetchUsers: OrganizationUsersContextProps['fetchUsers'] = useCallback(
    async (organizationId, params) => {
      dispatch({ type: 'FETCH_USERS' });
      try {
        const users = await httpClient.get<OrganizationUser[]>(
          `/organizations/${organizationId}/users`,
          { params }
        );
        dispatch({ type: 'FETCH_USERS_SUCCESS', payload: { users } });
        return users;
      } catch (error) {
        dispatch({
          type: 'FETCH_USER_FAILURE',
          payload: { error: `Failed to fetch users: ${error}` },
        });
        throw error;
      }
    },
    [httpClient]
  );

  const inviteUsers: OrganizationUsersContextProps['inviteUsers'] = useCallback(
    async (organizationId, userIds) => {
      dispatch({ type: 'INVITE_USERS', payload: { userIds } });
      try {
        const users = userIds
          .map((id) => state.entities[id])
          .map((u) => ({
            email: u.email,
            firstName: u.firstName,
            lastName: u.lastName,
            userId: u.userId,
            userRole: u.userRole,
          }));
        const { failed } = await httpClient.post<InviteUsersResponse>(
          `/organizations/${organizationId}/invite-users`,
          { users }
        );
        dispatch({
          type: 'INVITE_USERS_SUCCESS',
          payload: { userIds, failed },
        });
        return failed;
      } catch (error) {
        dispatch({ type: 'INVITE_USERS_FAILURE', payload: { error } });
        throw error;
      }
    },
    [httpClient, state.entities]
  );

  const createUser: OrganizationUsersContextProps['createUser'] = useCallback(
    async (organizationId, user) => {
      dispatch({ type: 'CREATE_USER' });
      try {
        const { failed } = await httpClient.post<InviteUsersResponse>(
          `/organizations/${organizationId}/invite-users`,
          { users: [user] }
        );
        dispatch({
          type: 'CREATE_USER_SUCCESS',
          payload: { userId: user.userId, failed },
        });
        return failed;
      } catch (error) {
        dispatch({ type: 'CREATE_USER_FAILURE', payload: { error } });
        throw error;
      }
    },
    [httpClient]
  );

  const uploadAvatar = useCallback(
    async (userId: string, file: File) => {
      const metadataResponse = await httpClient.post<{
        preSignedUrl: string;
        filekeyId: string;
        fileKey: string;
      }>(`/users/${userId}/avatar`, {
        filename: file.name,
        contentType: file.type,
        uploadType: 'PROFILE_PHOTO',
      });
      await fetch(metadataResponse.preSignedUrl, {
        body: file,
        method: 'PUT',
      });
    },
    [httpClient]
  );

  const updateUser: OrganizationUsersContextProps['updateUser'] = useCallback(
    async (userId, data, imageProfileFile) => {
      dispatch({ type: 'UPDATE_USER' });
      try {
        const updatedUser = await httpClient.put<OrganizationUser>(
          `/users/${userId}`,
          data
        );

        if (imageProfileFile) {
          await uploadAvatar(userId, imageProfileFile);
        }

        dispatch({
          type: 'UPDATE_USER_SUCCESS',
          payload: { user: updatedUser },
        });
      } catch (error) {
        dispatch({
          type: 'UPDATE_USER_FAILURE',
          payload: { error: `Error updating user: ${error}` },
        });
      }
    },
    [httpClient, uploadAvatar]
  );

  const setUserStatus: OrganizationUsersContextProps['setUserStatus'] =
    useCallback(
      async (userId, status) => {
        dispatch({ type: 'UPDATE_USER' });
        try {
          const updatedUser = await httpClient.put<OrganizationUser>(
            `/users/${userId}/status`,
            { status }
          );
          dispatch({
            type: 'UPDATE_USER_SUCCESS',
            payload: { user: updatedUser },
          });
        } catch (error) {
          dispatch({
            type: 'UPDATE_USER_FAILURE',
            payload: { error: `Error updating user status: ${error}` },
          });
          throw error;
        }
      },
      [httpClient]
    );

  const fetchUserById: OrganizationUsersContextProps['fetchUserById'] =
    useCallback(
      async (userId) => {
        dispatch({ type: 'FETCH_USER', payload: { userId } });

        try {
          const user = await httpClient.get<OrganizationUser>(
            `/users/${userId}`
          );
          dispatch({ type: 'FETCH_USER_SUCCESS', payload: { user } });
        } catch (error) {
          dispatch({
            type: 'FETCH_USER_FAILURE',
            payload: { error: `Error fetching user: ${error}` },
          });
        }
      },
      [httpClient]
    );

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  return (
    <OrganizationUsersContext.Provider
      value={{
        fetchUsers,
        fetchUserById,
        inviteUsers,
        createUser,
        updateUser,
        setUserStatus,
        clearError,
        users,
        currentUser,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
        error: state.error,
        inviteFailures: state.inviteFailures,
      }}
    >
      {children}
    </OrganizationUsersContext.Provider>
  );
};

export const useOrganizationUsers = () => {
  const context = useContext(OrganizationUsersContext);
  if (context === undefined) {
    throw new Error(
      'useOrganizationUsers must be used within a OrganizationUsersProvider'
    );
  }

  return context;
};
